// TourProvider — owns the registry of tours and the "currently active tour"
// state, exposes start/stop via context, and renders the active <Tour /> on
// top of the rest of the app.
//
// Tours are pure data (TourDefinition) — register them at the call site by
// passing a `tours` prop. Auto-start: if a registered tour's
// `shouldAutoStart` returns true and the user hasn't completed the current
// version, the provider starts it on mount.
//
// Lifecycle hooks (`onStart`, `onComplete`, `onSkip`) let the caller
// dispatch side-effects, e.g. seeding the builder with a sample expression
// when the tour starts.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import Tour from './Tour';
import {
  isTourCompleted,
  markTourCompleted,
} from '../../helpers/tourStorage';
import type { TourDefinition } from './types';

type TourLifecycle = {
  /** Fired once when a tour starts (regardless of trigger). */
  onStart?: (tourId: string) => void;
  /** Fired when a user reaches the final "Done" button. */
  onComplete?: (tourId: string) => void;
  /** Fired when a user skips or dismisses early. */
  onSkip?: (tourId: string) => void;
};

type RegisteredTour = TourDefinition & {
  shouldAutoStart?: () => boolean;
} & TourLifecycle;

type TourContextValue = {
  start: (tourId: string) => void;
  stop: () => void;
  active: string | null;
};

const TourContext = createContext<TourContextValue | null>(null);

export function useTourContext(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) {
    throw new Error(
      'useTourContext must be used inside a <TourProvider>',
    );
  }
  return ctx;
}

export type TourProviderProps = {
  /** All tours that should be reachable in this subtree. */
  tours: RegisteredTour[];
  children: ReactNode;
};

export default function TourProvider({ tours, children }: TourProviderProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const tourById = useMemo(() => {
    const map = new Map<string, RegisteredTour>();
    tours.forEach((t) => map.set(t.id, t));
    return map;
  }, [tours]);

  const start = useCallback(
    (id: string) => {
      const tour = tourById.get(id);
      if (!tour) {
        // Helps catch typos during development; in prod this just no-ops.
        // eslint-disable-next-line no-console
        console.warn(`[tour] unknown tour id: ${id}`);
        return;
      }
      tour.onStart?.(id);
      setActiveId(id);
    },
    [tourById],
  );

  const stop = useCallback(() => {
    setActiveId(null);
  }, []);

  // Auto-start pass: runs once after mount. We give the rest of the app a
  // tick to render so anchor selectors can resolve before the first step
  // queries them.
  useEffect(() => {
    const candidate = tours.find(
      (t) =>
        t.shouldAutoStart?.() === true &&
        !isTourCompleted(t.id, t.version),
    );
    if (!candidate) return;
    const handle = window.setTimeout(() => {
      candidate.onStart?.(candidate.id);
      setActiveId(candidate.id);
    }, 250);
    return () => window.clearTimeout(handle);
    // Intentional: only run once on mount. `tours` is stable for the
    // lifetime of the provider in practice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeTour = activeId ? tourById.get(activeId) ?? null : null;

  const handleComplete = useCallback(() => {
    if (!activeTour) return;
    markTourCompleted(activeTour.id, activeTour.version);
    activeTour.onComplete?.(activeTour.id);
    setActiveId(null);
  }, [activeTour]);

  const handleSkip = useCallback(() => {
    if (!activeTour) return;
    // Skipping ALSO marks completion — we don't want to nag users who chose
    // to dismiss. Replay button + version bump are the proper re-show paths.
    markTourCompleted(activeTour.id, activeTour.version);
    activeTour.onSkip?.(activeTour.id);
    setActiveId(null);
  }, [activeTour]);

  const ctxValue = useMemo<TourContextValue>(
    () => ({ start, stop, active: activeId }),
    [start, stop, activeId],
  );

  return (
    <TourContext.Provider value={ctxValue}>
      {children}
      {activeTour && (
        <Tour
          tour={activeTour}
          onComplete={handleComplete}
          onSkip={handleSkip}
        />
      )}
    </TourContext.Provider>
  );
}
